import { Box, Button, Text } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { memo, useCallback } from "react"
import { FaUserAstronaut } from "react-icons/fa"
import { FiLogOut, FiUser } from "react-icons/fi"

import useAuth from "@/hooks/useAuth"
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from "@chakra-ui/react"

export interface UserMenuProps {
  /** Custom user display name */
  userName?: string
  /** Additional menu items */
  additionalItems?: React.ReactNode
  /** Custom profile link destination */
  profilePath?: string
  /** Whether to show the user icon */
  showIcon?: boolean
}

/**
 * UserMenu Organism
 *
 * Dropdown menu for user actions including profile access and logout.
 * Displays the current user's name and provides quick access to user settings.
 */
export const UserMenu = memo<UserMenuProps>(
  ({
    userName,
    additionalItems,
    profilePath = "/settings",
    showIcon = true,
  }) => {
    const { user, logout } = useAuth()

    const handleLogout = useCallback(async () => {
      logout()
    }, [logout])

    const displayName = userName || user?.full_name || "User"

    return (
      <Box position="relative">
        <MenuRoot positioning={{ placement: "bottom-end" }}>
          <MenuTrigger asChild p={2}>
            <Button
              data-testid="user-menu"
              variant="solid"
              maxW="sm"
              truncate
              _hover={{ bg: "bg.emphasized" }}
            >
              {showIcon && <FaUserAstronaut fontSize="18" />}
              <Text>{displayName}</Text>
            </Button>
          </MenuTrigger>

          <MenuContent zIndex={20} minW="150px">
            <Link to={profilePath}>
              <MenuItem
                closeOnSelect
                value="user-settings"
                gap={2}
                py={2}
                cursor="pointer"
                data-testid="profile-menu-item"
              >
                <FiUser fontSize="18px" />
                <Box flex="1">My Profile</Box>
              </MenuItem>
            </Link>

            {additionalItems}

            <MenuItem
              value="logout"
              gap={2}
              py={2}
              onClick={handleLogout}
              cursor="pointer"
              data-testid="logout-menu-item"
            >
              <FiLogOut />
              Log Out
            </MenuItem>
          </MenuContent>
        </MenuRoot>
      </Box>
    )
  },
)

UserMenu.displayName = "UserMenu"
